import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Modal,
  Typography,
} from 'antd';
import InfiniteScroll from 'react-infinite-scroller';

import CommunityAPI from '../../../../api/CommunityAPI';
import Header from '../../../../components/Header/Header';
import SearchBar from '../../../../components/Header/SearchBar';
import LikedByUserSkeleton
  from '../../../../components/LikedByUsersModal/LikedByUserSkeleton';
import {
  setLikedByUsersModalState,
} from '../../../../context/communityReducer';
import { SEARCH_THRESHOLD } from '../../../../shared/constants';
import { DEFAULT_AVATAR } from '../../../../shared/data';
import showAppError from '../../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import { ILikedByUser } from '../../../../types/communityTypes';
import { ROUTES } from '../../../../types/routes';
import ChatAvatar from '../../../Messages/components/ChatAvatar/ChatAvatar';
import CreatorTag from '../../components/CreatorTag';

interface ILikedByUsersState {
  loading: boolean;
  hasMore: boolean;
  users: ILikedByUser[];
  page: number;
  limit: number;
}

const initialUsersState: ILikedByUsersState = {
  loading: false,
  hasMore: true,
  users: [],
  page: 1,
  limit: 25,
};

const CommunityPostLikedByUsersModal = () => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const {
    hostMetadata: {
      creator: { _id: hostCreatorId },
    },
  } = useAppSelector((state) => state.app);
  const { likedByUsersModalState } = useAppSelector((state) => state.community);

  const [searchValue, setSearchValue] = useState('');
  const [state, setState] = useState<ILikedByUsersState>(initialUsersState);

  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const fetchLikedByUsers = async (pageNumber: number = 1) => {
    const newState = { ...initialUsersState, ...state };

    try {
      const query = new URLSearchParams();
      query.append('page', pageNumber.toString());
      query.append('pageSize', '10');
      if (searchValue.length >= SEARCH_THRESHOLD) {
        query.append('search', searchValue);
      }
      const response = await CommunityAPI.getLikedByUsers(
        likedByUsersModalState?.channelId,
        likedByUsersModalState?.postId,
        query?.toString(),
      );
      if (response.status === 200) {
        const res = response.data;

        const list =
          (res.result &&
            'likes' in res.result &&
            Array.isArray(res.result.likes) && [...res.result.likes]) ||
          [];

        const newUsers = [...list];

        newState.users = [...newState.users, ...newUsers];
        newState.hasMore = newUsers.length >= newState.limit;

        console.log(newState.users);

        if (newState.hasMore) {
          newState.page += 1;
        }
      } else {
        showAppError(response, 'Failed to fetch liked by users');
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setState(newState);
    }
  };

  const closeLikedByUsersModal = () => {
    dispatch(
      setLikedByUsersModalState({
        visible: false,
        postId: '',
        channelId: '',
      }),
    );
  };

  const goToUserProfile = (userId: string) => {
    if (userId) {
      closeLikedByUsersModal();
      navigate(ROUTES.USER_PROFILE, {
        userId,
      });
    }
  };

  const sortedUsers: ILikedByUser[] = useMemo(() => {
    const creator = state.users.find((user) => user._id === hostCreatorId);

    if (creator) {
      return [
        creator,
        ...state.users.filter((user) => user._id !== hostCreatorId),
      ];
    }

    return state.users;
  }, [hostCreatorId, state.users]);

  const reloadLikedByUsers = () => {
    setState({ ...initialUsersState });
    fetchLikedByUsers();
  };

  useEffect(() => {
    const element = document.getElementById(
      'community-post-liked-by-users-modal',
    );
    console.log(element);
    if (element) {
      scrollContainerRef.current = element as HTMLElement;
    }
  }, []);

  useEffect(() => {
    if (likedByUsersModalState.visible) {
      reloadLikedByUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, likedByUsersModalState]);

  useEffect(() => {
    if (!likedByUsersModalState.visible) {
      setState({ ...initialUsersState });
    }
  }, [likedByUsersModalState]);

  return (
    <Modal
      key={likedByUsersModalState.postId}
      open={
        likedByUsersModalState.visible &&
        Boolean(likedByUsersModalState.postId) &&
        Boolean(likedByUsersModalState.channelId)
      }
      title={<Header title="Post Liked" handleBack={closeLikedByUsersModal} />}
      destroyOnClose
      className="messageInfoModal liked-by-users-modal"
      closable={false}
      closeIcon={null}
      footer={null}>
      <div className="liked-by-users-modal__search-wrapper">
        <SearchBar
          onDebounce={(value) => {
            if (value.length >= SEARCH_THRESHOLD) {
              setSearchValue(value);
            }
          }}
          placeholder="Search"
        />
        <div className="messageInfoModal__card liked-by-users-modal__card community-post-liked-by-users-modal">
          <InfiniteScroll
            pageStart={0}
            loadMore={() => fetchLikedByUsers(state.page)}
            hasMore={state.hasMore}
            loader={
              (state.loading ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    paddingTop: 0,
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      // gap: 12,
                      paddingTop: 0,
                    }}>
                    {Array(3)
                      .fill(0)
                      .map((_, index) => (
                        <LikedByUserSkeleton key={index} />
                      ))}
                  </div>
                </div>
              ) : null) as JSX.Element
            }
            useWindow={false}
            getScrollParent={() => scrollContainerRef.current as HTMLElement}>
            {/* > */}
            {sortedUsers.length > 0 ? (
              <>
                <Typography.Title
                  level={5}
                  className="messageInfoModal__card__title">
                  LIKED BY
                </Typography.Title>
                <div className="liked-by-users-modal__card__users-list">
                  {sortedUsers.map((user) => (
                    <div
                      className="liked-by-users-modal__card__users-list__item"
                      onClick={() => goToUserProfile(user._id)}>
                      <ChatAvatar
                        url={user.profilePicUrl || DEFAULT_AVATAR}
                        size={32}
                        style={{ flexShrink: 0 }}
                      />

                      <div className="liked-by-users-modal__card__users-list__item__text-wrapper">
                        <Typography.Title
                          level={5}
                          ellipsis={{ rows: 1, expandable: false }}
                          className="text">
                          {user.name}
                        </Typography.Title>
                        {user._id === hostCreatorId && <CreatorTag />}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Typography.Title
                level={5}
                className="messageInfoModal__card__title">
                {state.loading
                  ? 'LOADING...'
                  : searchValue
                  ? `NO LIKES FOUND`
                  : `NO LIKES YET`}
              </Typography.Title>
            )}
          </InfiniteScroll>
        </div>
      </div>
    </Modal>
  );
};

export default CommunityPostLikedByUsersModal;
